import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Copy, Check, ArrowUpRight, Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EASE, scrollToId } from "./common";
import { SCHOLAR_IDS, CV_PDF_URL } from "../data/cv";
import { useLang, t } from "../i18n";

const PORTRAIT_URL = "https://customer-assets-jt897jd0.emergentagent.net/job_academic-cv-5/artifacts/u3z2n2n7_SELF-PIC.jpg";

const MaskedLine = ({ children, delay, className }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

const Badge = ({ b, i }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(b.value);
    setCopied(true);
    toast.success(`${b.label} ID copied`, { description: b.value });
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <motion.button
      data-testid={`scholar-badge-${b.key}`}
      onClick={copy}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.1 + i * 0.1, duration: 0.7, ease: EASE }}
      whileHover={{ y: -4 }}
      className="group flex items-center gap-3 bg-white/70 backdrop-blur border border-ink/10 rounded-full pl-2 pr-4 py-1.5 text-left hover:border-crimson/40 transition-colors"
      title="Click to copy"
    >
      <span className="w-7 h-7 rounded-full bg-ink text-bone grid place-items-center font-mono text-[9px] font-bold">
        {b.label.slice(0, 2)}
      </span>
      <span className="leading-tight">
        <span className="block font-mono text-[9px] uppercase tracking-[0.2em] text-mutedink">{b.label}</span>
        <span className="block font-mono text-[11px] text-ink">{b.value}</span>
      </span>
      {copied ? <Check size={13} className="text-emerald" /> : <Copy size={13} className="text-ink/30 group-hover:text-crimson transition-colors" />}
    </motion.button>
  );
};

const Hero = () => {
  const { lang } = useLang();
  const HERO_LINES = [t(lang, "hero_line_1"), t(lang, "hero_line_2"), t(lang, "hero_line_3")];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBig = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const ySub = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen bg-bone bg-grid-light overflow-hidden flex flex-col" data-testid="hero-section">
      <motion.div
        style={{ y: yBig }}
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-6 sm:bottom-0 inset-x-0 text-center font-display font-black italic text-[19vw] leading-none text-outline-light select-none"
      >
        PSEPHOLOGY
      </motion.div>
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-sand/70 to-transparent pointer-events-none" />
      <div className="absolute top-24 -left-10 w-72 h-72 rounded-full bg-crimson/[0.07] blur-3xl pointer-events-none" />
      <div className="absolute bottom-32 right-10 w-96 h-96 rounded-full bg-saffron/[0.08] blur-3xl pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full flex-1 flex flex-col justify-center pt-32 pb-40">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="w-2.5 h-2.5 rounded-full bg-emerald pulse-dot" />
          <span className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-mutedink" data-testid="hero-status">
            {t(lang, "hero_status")}
          </span>
        </motion.div>

        <h1 className="font-display font-bold tracking-tight text-ink text-[13vw] sm:text-7xl lg:text-8xl leading-[0.98] mb-2">
          <MaskedLine delay={0.15} className="">{t(lang, "hero_name_1")}</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-crimson">{t(lang, "hero_name_2")}</MaskedLine>
        </h1>

        <div className="mt-6 font-display text-2xl sm:text-4xl lg:text-5xl text-ink/85 leading-tight">
          {HERO_LINES.map((l, i) => (
            <MaskedLine key={l} delay={0.55 + i * 0.14}>
              {l}
              {i === 2 && <span className="text-saffron">.</span>}
            </MaskedLine>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.8, ease: EASE }}
          className="mt-8 max-w-xl text-mutedink text-base sm:text-lg leading-relaxed"
          data-testid="hero-subheadline"
        >
          {t(lang, "hero_sub_a")}<span className="text-ink font-semibold">CHUNAV AI</span>{t(lang, "hero_sub_b")}
          <span className="text-ink font-semibold">{lang === "hi" ? "बनारस हिन्दू विश्वविद्यालय, वाराणसी" : "Banaras Hindu University, Varanasi"}</span>{t(lang, "hero_sub_c")}
        </motion.p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          {SCHOLAR_IDS.map((b, i) => (
            <Badge key={b.key} b={b} i={i} />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 0.8, ease: EASE }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <button
            data-testid="hero-explore-research-btn"
            onClick={() => scrollToId("focus")}
            className="inline-flex items-center gap-2 bg-crimson text-bone font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:bg-saffron transition-colors duration-300"
          >
            {t(lang, "hero_explore")} <ArrowDown size={14} />
          </button>
          <a
            data-testid="hero-electbharat-btn"
            href="https://www.electbharat.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-ink/25 text-ink font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:bg-ink hover:text-bone transition-colors duration-300"
          >
            electbharat.com <ArrowUpRight size={14} />
          </a>
          <a
            data-testid="hero-download-cv-btn"
            href={CV_PDF_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-crimson/40 text-crimson font-mono text-xs uppercase tracking-[0.2em] px-7 py-4 rounded-full hover:bg-crimson hover:text-bone transition-colors duration-300"
          >
            {t(lang, "hero_cv")} <Download size={14} />
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30, rotate: 4 }}
        animate={{ opacity: 1, y: 0, rotate: 2 }}
        transition={{ delay: 1.7, duration: 1, ease: EASE }}
        className="absolute right-8 top-36 z-10 hidden xl:block"
        data-testid="hero-id-card"
      >
        <motion.div
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="w-80 bg-panel/90 backdrop-blur border border-ink/15 rounded-2xl p-6 shadow-2xl"
        >
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <img src={PORTRAIT_URL} alt="Dr. Abhishek Verma portrait" data-testid="hero-portrait" className="w-11 h-11 rounded-xl object-cover border border-ink/15" />
              <div>
                <p className="font-display font-bold text-ink leading-tight">Abhishek Verma</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mutedink">Ph.D. · PDF · BHU</p>
              </div>
            </div>
            <span className="w-2 h-2 rounded-full bg-emerald pulse-dot" />
          </div>
          <div className="space-y-2.5 font-mono text-[10px] uppercase tracking-[0.15em]">
            <div className="flex justify-between border-b border-ink/10 pb-2">
              <span className="text-mutedink">ORCID</span>
              <span className="text-ink">0000-0003-0298-2194</span>
            </div>
            <div className="flex justify-between border-b border-ink/10 pb-2">
              <span className="text-mutedink">Field</span>
              <span className="text-crimson">Psephology / AI</span>
            </div>
            <div className="flex justify-between border-b border-ink/10 pb-2">
              <span className="text-mutedink">Base</span>
              <span className="text-ink">BHU · Varanasi</span>
            </div>
            <div className="flex justify-between">
              <span className="text-mutedink">IP Assets</span>
              <span className="text-saffron">3 Patents · 2 CR</span>
            </div>
          </div>
          <div className="barcode h-10 mt-5 text-ink/70" aria-hidden="true" />
          <p className="mt-2 font-mono text-[9px] tracking-[0.3em] text-mutedink text-center">SCHOLAR-ID // AV-2026-PDF</p>
        </motion.div>
      </motion.div>

      <motion.div style={{ y: ySub }} className="absolute bottom-8 left-5 sm:left-8 z-10 flex items-center gap-3 text-mutedink">
        <MapPin size={14} className="text-crimson" />
        <span className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em]">{t(lang, "hero_location")}</span>
      </motion.div>
    </section>
  );
};

export default Hero;
